import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { PageHeader, AIBadge } from '@/components/AIBadge';
import { INSPECTOR } from '@/constants/testIds';
import api from '@/lib/api';
import { Camera, Loader2, Send, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const ANSWER_OPTIONS = [
    { value: 'yes', label: 'Sí', color: '#00875A', testId: INSPECTOR.answerYes },
    { value: 'no', label: 'No', color: '#E53935', testId: INSPECTOR.answerNo },
    { value: 'na', label: 'N/A', color: '#6B7280', testId: INSPECTOR.answerNA },
];

export default function ChecklistPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [checklist, setChecklist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingQid, setSavingQid] = useState(null);
    const [uploadingQid, setUploadingQid] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const fileRefs = useRef({});

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/checklists/${id}`);
            setChecklist(data);
        } catch (e) {
            toast.error('No se pudo cargar el checklist');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [id]);

    const updateAnswer = async (qid, patch) => {
        setSavingQid(qid);
        try {
            const q = checklist.questions.find((x) => x.id === qid);
            const payload = {
                answer: patch.answer ?? q.answer ?? '',
                observations: patch.observations ?? q.observations ?? '',
            };
            const { data } = await api.patch(`/checklists/${id}/questions/${qid}`, payload);
            setChecklist((prev) => ({
                ...prev,
                score: data.score,
                questions: prev.questions.map((x) => x.id === qid ? { ...x, ...payload } : x),
            }));
        } catch (e) {
            toast.error('Error al guardar respuesta');
        } finally {
            setSavingQid(null);
        }
    };

    const uploadEvidence = async (qid, file) => {
        if (!file) return;
        setUploadingQid(qid);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post(`/checklists/${id}/questions/${qid}/evidence`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setChecklist((prev) => ({
                ...prev,
                questions: prev.questions.map((x) =>
                    x.id === qid ? { ...x, evidences: [...(x.evidences || []), data] } : x
                ),
            }));
            toast.success('Evidencia subida. IA analizando...');
        } catch (e) {
            toast.error('Error al subir evidencia');
        } finally {
            setUploadingQid(null);
        }
    };

    const submitAll = async () => {
        const unanswered = checklist.questions.filter((q) => !q.answer);
        if (unanswered.length > 0) {
            toast.error(`Faltan ${unanswered.length} preguntas por responder`);
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/checklists/${id}/submit`);
            toast.success('Checklist enviado para dictamen');
            navigate('/inspector');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al enviar');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !checklist) {
        return (
            <DashboardLayout role="inspector">
                <div className="p-12 text-center text-gray-500">Cargando checklist...</div>
            </DashboardLayout>
        );
    }

    const answered = checklist.questions.filter((q) => q.answer).length;
    const progress = (answered / checklist.questions.length) * 100;

    return (
        <DashboardLayout role="inspector">
            <PageHeader
                title={checklist.distintivo_name || 'Distintivo'}
                subtitle="◆ Auditoría en curso"
                accent="#00875A"
            >
                <AIBadge>Generado por IA</AIBadge>
            </PageHeader>

            {/* Progress + Score */}
            <div className="border border-gray-200 p-6 mb-6 bg-white grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                    <div className="label-xs mb-1">Avance</div>
                    <div className="font-display text-2xl font-bold">{answered} / {checklist.questions.length}</div>
                    <div className="w-full bg-gray-200 h-2 mt-2">
                        <div className="bg-[#00875A] h-2" style={{ width: `${progress}%` }} />
                    </div>
                </div>
                <div data-testid={INSPECTOR.scoreDisplay}>
                    <div className="label-xs mb-1">Puntaje en tiempo real</div>
                    <div className="font-display text-2xl font-bold">{(checklist.score ?? 0).toFixed(1)}%</div>
                    <div className="text-xs text-gray-500 mt-1">N/A no cuenta · peso se redistribuye</div>
                </div>
                <div>
                    <div className="label-xs mb-1">Fuentes IA</div>
                    <div className="text-sm">{checklist.sources?.join(' · ')}</div>
                </div>
            </div>

            {/* Questions */}
            <div className="space-y-4">
                {checklist.questions.map((q, idx) => (
                    <div key={q.id} className="border border-gray-200 p-6 bg-white">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="label-xs">Pregunta {idx + 1}</span>
                                    <span className="text-xs bg-gray-100 px-2 py-0.5 font-medium">Peso: {q.weight.toFixed(1)}%</span>
                                    <AIBadge>IA</AIBadge>
                                </div>
                                <div className="font-medium leading-relaxed">{q.question}</div>
                            </div>
                        </div>

                        {/* Answers */}
                        <div className="flex gap-2 mb-4">
                            {ANSWER_OPTIONS.map((opt) => {
                                const active = q.answer === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        data-testid={`${opt.testId}-${idx}`}
                                        onClick={() => updateAnswer(q.id, { answer: opt.value })}
                                        disabled={savingQid === q.id || checklist.submitted}
                                        style={active ? { background: opt.color, borderColor: opt.color, color: 'white' } : {}}
                                        className={`flex-1 md:flex-initial md:px-8 py-3 border font-medium text-sm transition-colors ${
                                            active ? '' : 'border-gray-300 hover:border-black bg-white'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Observations */}
                        <textarea
                            data-testid={`${INSPECTOR.observationsInput}-${idx}`}
                            placeholder="Observaciones del auditor (opcional)..."
                            defaultValue={q.observations}
                            onBlur={(e) => {
                                if (e.target.value !== q.observations) {
                                    updateAnswer(q.id, { observations: e.target.value });
                                }
                            }}
                            disabled={checklist.submitted}
                            rows={2}
                            className="w-full border border-gray-300 px-3 py-2 text-sm focus:border-black outline-none mb-3 resize-none"
                        />

                        {/* Evidence */}
                        <div className="border-t border-gray-100 pt-3">
                            <div className="flex items-center justify-between mb-3">
                                <div className="label-xs">Evidencias ({q.evidences?.length || 0})</div>
                                <label className={`inline-flex items-center gap-2 text-sm font-medium cursor-pointer border border-gray-300 hover:border-[#00875A] hover:text-[#00875A] px-3 py-2 transition-colors ${checklist.submitted ? 'opacity-50 pointer-events-none' : ''}`}>
                                    {uploadingQid === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                                    Adjuntar foto
                                    <input
                                        ref={(r) => (fileRefs.current[q.id] = r)}
                                        type="file" accept="image/*" className="hidden"
                                        data-testid={`${INSPECTOR.uploadEvidence}-${idx}`}
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) uploadEvidence(q.id, f);
                                            e.target.value = '';
                                        }}
                                    />
                                </label>
                            </div>

                            {q.evidences && q.evidences.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {q.evidences.map((ev) => (
                                        <EvidenceCard key={ev.id} evidence={ev} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Submit Bar */}
            {!checklist.submitted && (
                <div className="sticky bottom-0 left-0 right-0 -mx-6 mt-8 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-4 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
                    <div className="text-sm">
                        <span className="font-medium">{answered} / {checklist.questions.length}</span> respondidas ·
                        <span className="font-medium ml-1">Puntaje: {(checklist.score ?? 0).toFixed(1)}%</span>
                    </div>
                    <button
                        onClick={submitAll}
                        disabled={submitting || answered < checklist.questions.length}
                        data-testid={INSPECTOR.submitChecklist}
                        className="bg-[#0A0A0A] hover:bg-black text-white px-6 py-3 font-medium inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Enviar para dictamen
                    </button>
                </div>
            )}
        </DashboardLayout>
    );
}

function EvidenceCard({ evidence }) {
    const [blobUrl, setBlobUrl] = useState(null);

    useEffect(() => {
        let mounted = true;
        api.get(`/files/${evidence.storage_path}`, { responseType: 'blob' })
            .then((r) => {
                if (!mounted) return;
                const url = URL.createObjectURL(r.data);
                setBlobUrl(url);
            }).catch(() => {});
        return () => { mounted = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
        // eslint-disable-next-line
    }, [evidence.storage_path]);

    return (
        <div className="border border-gray-200 bg-gray-50 p-3">
            <div className="aspect-video bg-gray-200 mb-2 overflow-hidden flex items-center justify-center">
                {blobUrl ? (
                    <img src={blobUrl} alt="evidencia" className="w-full h-full object-cover" />
                ) : (
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                )}
            </div>
            <div className="ai-badge mb-2">Análisis IA</div>
            <p className="text-xs text-gray-700 leading-relaxed mb-2">{evidence.ai_analysis}</p>
        </div>
    );
}
